import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import DeliveryPicker from "@/components/orders/DeliveryPicker";
import { meApi, pricingApi, type DeliveryInput } from "@/lib/api";
import { DEFAULT_PRICING, type Pricing, type PrintMode } from "@/lib/pricing";
import { money } from "@/lib/order";

export default function NewOrderPage() {
  const [params] = useSearchParams();
  const designId = params.get("design") || "";
  const unitPrice = Number(params.get("price")) || 0;
  const printMode = (params.get("mode") as PrintMode) || "print_on_garment";
  const sizeLabel = params.get("size") || "";
  const navigate = useNavigate();

  const [design, setDesign] = useState<any | null>(null);
  const [pricing, setPricing] = useState<Pricing>(DEFAULT_PRICING);
  const [loading, setLoading] = useState(true);
  const [qty, setQty] = useState(1);
  const [note, setNote] = useState("");
  const [delivery, setDelivery] = useState<DeliveryInput>({ method: "courier" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    pricingApi.get().then((r) => setPricing(r.pricing)).catch(() => {});
    // Pre-fill delivery from the saved profile (still editable per order).
    meApi
      .profile()
      .then((r) => {
        const p = r.profile;
        if (p && (p.phone || p.address || p.locker_code)) {
          setDelivery({
            method: p.delivery_method || (p.locker_code ? "pudo" : "courier"),
            phone: p.phone || "",
            address: p.address || "",
            locker_code: p.locker_code || "",
            locker_name: p.locker_name || "",
          });
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!designId) {
      setLoading(false);
      return;
    }
    meApi
      .design(designId)
      .then((r) => setDesign(r.design))
      .catch(() => setDesign(null))
      .finally(() => setLoading(false));
  }, [designId]);

  const deliveryFee = useMemo(
    () => (delivery.method === "pudo" ? pricing.delivery.pudo : delivery.method === "courier" ? pricing.delivery.courier : 0),
    [delivery.method, pricing]
  );
  const itemsTotal = unitPrice * qty;
  const estTotal = itemsTotal + deliveryFee;

  async function submit() {
    setError("");
    if (!design) {
      setError("Pick a saved design first.");
      return;
    }
    if (delivery.method === "courier" && !(delivery.address || "").trim()) {
      setError("Please enter a delivery address for Courier Guy.");
      return;
    }
    if (delivery.method === "pudo" && !delivery.locker_code) {
      setError("Please choose a Pudo locker.");
      return;
    }
    if (!(delivery.phone || "").trim()) {
      setError("Please enter a contact number.");
      return;
    }
    setBusy(true);
    try {
      const { id } = await meApi.createOrder({
        items: [
          {
            design_id: design.id,
            kind: design.kind === "gangsheet" ? "gangsheet" : "tshirt",
            description: sizeLabel ? `${design.name} — ${sizeLabel}` : design.name,
            qty,
            unit_price: unitPrice,
            print_mode: printMode,
            size_label: sizeLabel,
            garment_color: design.color || "",
            thumbnail: design.thumbnail || "",
          },
        ],
        customer_note: note,
        delivery,
        delivery_fee: deliveryFee,
      });
      navigate(`/orders/${id}`);
    } catch (e: any) {
      setError(e.message || "Could not create your order");
      setBusy(false);
    }
  }

  const box = "rounded-2xl bg-[#11192b] border border-sky-500/10 p-6";

  return (
    <>
      <Navbar />
      <main className="min-h-screen bg-[#0b111c] text-white">
        <div className="mx-auto max-w-3xl px-6 py-12">
          <Link to="/studio" className="text-sky-400 text-sm hover:underline">
            ← Back to studio
          </Link>
          <h1 className="text-4xl font-black mt-2">Request a Quote</h1>
          <p className="text-gray-400 mt-1">
            We'll review your design and send you a quote. Nothing is charged online.
          </p>

          {loading ? (
            <div className={`${box} mt-8 text-gray-400`}>Loading your design…</div>
          ) : !design ? (
            <div className={`${box} mt-8 text-gray-400`}>
              No design selected.{" "}
              <Link to="/studio" className="text-sky-400 hover:underline">
                Open the studio
              </Link>{" "}
              and hit “Save &amp; Request Quote”.
            </div>
          ) : (
            <div className="mt-8 space-y-6">
              <div className={box}>
                <h2 className="font-bold text-lg mb-4">Your design</h2>
                <div className="flex items-center gap-4">
                  <div className="h-24 w-24 rounded-lg bg-[#0b111c] overflow-hidden flex items-center justify-center shrink-0">
                    {design.thumbnail ? (
                      <img src={design.thumbnail} className="max-h-full max-w-full object-contain" />
                    ) : (
                      <span className="text-3xl">🎨</span>
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold">{design.name}</p>
                    <p className="text-xs text-gray-500 flex items-center gap-2">
                      {design.kind === "gangsheet" ? "Gang Sheet" : "T-Shirt"}
                      {design.kind !== "gangsheet" && design.color && (
                        <span className="inline-flex items-center gap-1">
                          ·
                          <span
                            className="inline-block h-3 w-3 rounded-full border border-white/30"
                            style={{ backgroundColor: design.color }}
                          />
                          shirt colour
                        </span>
                      )}
                    </p>
                    <label className="mt-3 flex items-center gap-2 text-sm text-gray-400">
                      Quantity
                      <input
                        type="number"
                        min={1}
                        value={qty}
                        onChange={(e) => setQty(Math.max(1, Number(e.target.value) || 1))}
                        className="w-24 rounded-lg bg-[#0b111c] border border-sky-500/20 px-3 py-2 text-white"
                      />
                    </label>
                  </div>
                </div>
              </div>

              <div className={box}>
                <h2 className="font-bold text-lg mb-4">Delivery</h2>
                <DeliveryPicker value={delivery} onChange={setDelivery} />
              </div>

              <div className={box}>
                <h2 className="font-bold text-lg mb-4">Notes (optional)</h2>
                <textarea
                  rows={3}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Anything we should know — sizes, colours, deadlines…"
                  className="w-full rounded-lg bg-[#0b111c] border border-sky-500/20 px-4 py-3 text-white placeholder-gray-500 focus:border-sky-400 outline-none"
                />
              </div>

              <div className={box}>
                <h2 className="font-bold text-lg mb-4">Estimate</h2>
                <div className="space-y-2 text-sm">
                  <Row label={`${design.name}${sizeLabel ? ` · ${sizeLabel.split(" —")[0]}` : ""} × ${qty}`} value={money(itemsTotal)} />
                  {design.kind !== "gangsheet" && (
                    <Row
                      label={printMode === "print_only" ? "Supply print only" : "Printed onto garment"}
                      value=""
                      muted
                    />
                  )}
                  <Row label={`Delivery (${delivery.method === "pudo" ? "Pudo locker" : "Courier Guy"})`} value={money(deliveryFee)} />
                  <div className="border-t border-sky-500/10 pt-2 mt-2 flex justify-between font-bold text-base">
                    <span>Estimated total</span>
                    <span>{money(estTotal)}</span>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-3">
                  This is an estimate. We'll review your artwork and send you a final quote to accept before payment.
                </p>
              </div>

              {error && <p className="text-rose-400">{error}</p>}

              <button
                onClick={submit}
                disabled={busy}
                className="w-full rounded-xl bg-sky-500 px-6 py-4 font-bold text-white hover:bg-sky-400 disabled:opacity-60"
              >
                {busy ? "Submitting…" : "Submit for a quote"}
              </button>
            </div>
          )}
        </div>
      </main>
    </>
  );
}

function Row({ label, value, muted }: { label: string; value: string; muted?: boolean }) {
  return (
    <div className={`flex justify-between ${muted ? "text-gray-500" : "text-gray-300"}`}>
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}
